import { useEffect, useState } from "react"
import { useDimension } from "../store/dimensions-store"
import { cardHeight, cardWidth } from "@/features/card/ui/Card"
import { useSignal, viewport } from "@telegram-apps/sdk-react"

export interface DimensionProps {
  cardHeight: number
  cardScale: number
  cardWidth: number
  height: number
  isHorizontal: boolean
  playerSize: number
  width: number
}

export const useInitDimensions = () => {
  const updateDimension = useDimension((state) => state.updateDimension)
  const updateIsDark = useDimension((state) => state.updateIsDark)
  const updateTopSafeArea = useDimension((state) => state.updateTopSafeArea)
  const updateBottomSafeArea = useDimension(
    (state) => state.updateBottomSafeArea
  )

  const [isDark, setIsDark] = useState(false)
  const [topSafeArea, setTopSafeArea] = useState(0)
  const [dimensions, setDimensions] = useState<DimensionProps>({
    cardHeight,
    cardScale: 1,
    cardWidth,
    height: window.innerHeight,
    isHorizontal: false,
    playerSize: 40,
    width: window.innerWidth,
  })

  useEffect(() => {
    const handleResize = () => {
      const cardScale = (window.innerHeight - topSafeArea) / 3.2 / cardHeight

      const newDimensions = {
        cardHeight: cardScale * cardHeight,
        cardScale,
        cardWidth: cardScale * cardWidth,
        height: window.innerHeight,
        isHorizontal: false,
        playerSize: (cardHeight * cardScale) / 2.6,
        width: window.innerWidth,
      }

      setDimensions(newDimensions)
      updateDimension(newDimensions)
    }
    handleResize()

    window.addEventListener("resize", handleResize)

    return () => window.removeEventListener("resize", handleResize)
  }, [updateDimension])

  useEffect(() => {
    const updateDarkMode = () => {
      setIsDark(document.documentElement.classList.contains("dark"))
      updateIsDark(document.documentElement.classList.contains("dark"))
    }

    updateDarkMode()

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === "class") updateDarkMode()
      })
    })

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    })

    return () => observer.disconnect()
  }, [updateIsDark])

  useEffect(() => {
    const handleSafeArea = () => {
      const rootStyles = getComputedStyle(document.documentElement)
      const insetTop1 =
        Number.parseFloat(
          rootStyles.getPropertyValue(
            "--tg-viewport-content-safe-area-inset-top"
          )
        ) || viewport.safeAreaInsetTop()
      const insetTop2 =
        Number.parseFloat(
          rootStyles.getPropertyValue("--tg-viewport-safe-area-inset-top")
        ) || viewport.contentSafeAreaInsetTop()
      const insetBottom =
        Number.parseFloat(
          rootStyles.getPropertyValue(
            "--tg-viewport-content-safe-area-inset-bottom"
          )
        ) || viewport.safeAreaInsetBottom()
      const insetTop = insetTop1 + insetTop2

      setTopSafeArea(insetTop)
      updateTopSafeArea(insetTop)
      updateBottomSafeArea(insetBottom)
    }

    handleSafeArea()
    window.addEventListener("resize", handleSafeArea)
    return () => window.removeEventListener("resize", handleSafeArea)
  }, [updateBottomSafeArea, updateTopSafeArea])

  return { dimensions, isDark, topSafeArea }
}
